import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { authApi } from "../services/auth";
import "./LoginPage.css";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { t } from "../i18n";
import { T } from "../i18n/T";

const ResetPasswordPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  // The emailed link carries ?from=app when the reset was requested inside
  // the iOS/Android app — this page always opens in a browser, so on success
  // we point those talents back to the app instead of the web login.
  const [searchParams] = useSearchParams();
  const fromApp = searchParams.get("from") === "app";
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  // Which account this link resets — fetched from the token so we can show
  // "For dj@example.com". null until known; "" if the lookup fails softly.
  const [accountEmail, setAccountEmail] = useState<string | null>(null);
  // A hard "this link is dead" state, shown up front (before they type a
  // password) when the token is invalid or expired.
  const [linkDead, setLinkDead] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await authApi.resetPasswordInfo(String(token));
        if (active) setAccountEmail(res?.email || "");
      } catch (err: any) {
        // 400 = token invalid/expired → show the dead-link state. Any other
        // failure (endpoint missing on an older server, network) degrades
        // silently: the form still works, just without the "For …" line.
        if (active && err?.response?.status === 400) setLinkDead(true);
        else if (active) setAccountEmail("");
      }
    })();
    return () => {
      active = false;
    };
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    try {
      await authApi.resetPassword(String(token), password);
      setSuccess(true);
    } catch (err: any) {
      // Surface the real reason — a weak password is NOT an expired link.
      setError(err?.response?.data?.msg || t("resetPasswordPage.invalidOrExpiredLink"));
    }
  };

  if (linkDead) {
    return (
      <div className="login-container">
        <div className="login-inner">
          <h1 className="login-hero-title">{t("resetPasswordPage.linkExpired")}</h1>
          <p className="login-hero-tagline">
            {t("resetPasswordPage.thisResetLinkIs")}
          </p>
          <button
            className="login-button"
            style={{ marginTop: "1.25rem" }}
            onClick={() => navigate("/forgot-password")}
          >
            {t("resetPasswordPage.getANewLink")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="login-container">
      <div className="login-inner">
        {success ? (
          <>
            <h1 className="login-hero-title">{t("resetPasswordPage.passwordUpdated")}</h1>
            {fromApp ? (
              <p className="login-hero-tagline">
                <T k="resetPasswordPage.openRequestAppAnd" tags={{ 0: (c) => <strong>{c}</strong> }} />
              </p>
            ) : (
              <button
                className="login-button"
                style={{ marginTop: "1.25rem" }}
                onClick={() => navigate("/login")}
              >
                {t("resetPasswordPage.backToSignIn")}
              </button>
            )}
          </>
        ) : (
          <>
            <h1 className="login-hero-title">{t("resetPasswordPage.setNewPassword")}</h1>
            {accountEmail && (
              <p className="login-hero-tagline">
                <T k="resetPasswordPage.for" vars={{ accountEmail }} tags={{ 0: (c) => <strong>{c}</strong> }} />
              </p>
            )}
            {error && <p className="login-error">{error}</p>}
            <form onSubmit={handleSubmit} className="login-form">
              <div className="input-wrapper">
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder={t("resetPasswordPage.newPassword")}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoFocus
                  className="login-input"
                />
                <span
                  className="clear-icon"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </span>
              </div>
              <button type="submit" className="login-button">
                {t("resetPasswordPage.changePassword")}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default ResetPasswordPage;
